// Device usage, as a section of the admin dashboard.
//
// No route and no login of its own: it renders inside AdminDashboard and rides
// the same session as every other view there. Picking a resident swaps this
// component between the list and one resident's detail rather than navigating.
//
// The visual language comes from the "Device Usage Dashboard" design and is
// scoped to .sc-usage — the surrounding admin app is Tailwind.

import React, { useCallback, useEffect, useState } from "react";
import BreakdownPanel, { DailyDurationChart, Histogram, ProgressList, StatCard } from "./BreakdownPanel";
import FamilyPanel from "./FamilyPanel";
import { fetchUsage, formatCount, formatDuration, formatLastInteraction } from "./analyticsApi";
import "./usageDashboard.css";
import { ageFromBirthdate } from "../../utils/patientAge";

const RANGES = [7, 30, 90];

const DIMENSIONS = {
  activities: {
    label: "Aktivitäten",
    showDuration: true,
    // Shared end-of-game scoreboard; "which game was finished" is what this
    // dimension answers properly.
    hide: ["quiz_result"],
  },
  functions: { label: "Funktionen" },
  screens: { label: "Bildschirme", showDuration: true },
  touches: { label: "Touch" },
};

const START_SPLIT_LABELS = {
  self_voice: "Selbst — Sprache",
  self_touch: "Selbst — Bildschirm",
  offered_voice: "Vorgeschlagen — per Sprache",
  offered_touch: "Vorgeschlagen — per Touch",
  unknown: "Unbekannt",
};

const INITIATOR_LABELS = {
  button: "Knopf",
  wakeword: "Weckwort",
  touch: "Bildschirm",
  device: "Gerät",
  unknown: "Unbekannt",
};
const ENDED_BY_LABELS = {
  button: "Knopf",
  command: "Sprachbefehl",
  idle: "Zeitüberschreitung",
  call: "Anruf",
  video: "Video",
  unknown: "Unbekannt",
};

// The design uses Manrope. Injected once from here so the whole look travels
// with this component rather than being wired into the app's index.html.
function useManrope() {
  useEffect(() => {
    const href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap";
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, []);
}

function percent(rate) {
  return rate === null || rate === undefined ? "—" : `${Math.round(rate * 100)}%`;
}

function attention(sessions) {
  // A resident who has stopped using the device is the one thing this view
  // exists to surface, so it gets a chip rather than a column to read.
  if (!sessions.count) return { label: "Keine Nutzung", tone: "bad" };
  if (sessions.days_since_last >= 7) return { label: `${sessions.days_since_last} Tage inaktiv`, tone: "bad" };
  if (sessions.days_since_last >= 3) return { label: `${sessions.days_since_last} Tage inaktiv`, tone: "warn" };
  return null;
}

function RangeToggle({ days, onChange }) {
  return (
    <div className="segmented">
      {RANGES.map((value) => (
        <button key={value} className={value === days ? "active" : ""} onClick={() => onChange(value)}>
          {value} Tage
        </button>
      ))}
    </div>
  );
}

function RoutineOffers({ data }) {
  if (!data || !data.subjects.length) {
    return <div className="empty-note">In diesem Zeitraum wurde nichts vorgeschlagen.</div>;
  }
  const max = Math.max(1, ...data.subjects.map((row) => row.offered));

  return (
    <>
      <div className="stat-grid stat-grid-3">
        <StatCard label="Vorgeschlagen" value={formatCount(data.offered)} />
        <StatCard label="Angenommen" value={formatCount(data.accepted)} />
        <StatCard label="Annahmequote" value={percent(data.rate)} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <div className="detail-hist-label">Vorgeschlagen nach Tageszeit</div>
        <Histogram hours={data.by_hour} />
      </div>

      <div className="bar-legend">
        <span className="bar-legend-item">
          <span className="mix-dot" style={{ background: "var(--accent)" }} />
          angenommen
        </span>
        <span className="bar-legend-item">
          <span className="mix-dot" style={{ background: "var(--declined)" }} />
          nicht angenommen
        </span>
      </div>

      <div>
        {data.subjects.map((row, index) => (
          <div key={row.subject} className="lb-row routine-row">
            <div className={`lb-rank ${index < 3 ? "lb-rank-top" : ""}`}>{index + 1}</div>
            <div className="lb-name">{row.subject}</div>
            <div className="lb-bar-track">
              <div className="lb-bar-split" style={{ width: `${(row.offered / max) * 100}%` }}>
                {/* Clamped: an offer made just before the window and accepted
                    inside it would otherwise push the blue past the bar. */}
                <div
                  className="lb-seg-accepted"
                  style={{ width: `${Math.min(1, row.accepted / (row.offered || 1)) * 100}%` }}
                />
                <div className="lb-seg-declined" style={{ flex: 1 }} />
              </div>
            </div>
            <div className="lb-value">{formatCount(row.offered)} vorgeschlagen</div>
            <div className="lb-value">{formatCount(row.accepted)} angenommen</div>
            <div className="lb-quote">{percent(row.rate)}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function ResidentDetail({ patientId, days, onDaysChange, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reloading, setReloading] = useState(false);
  // Two different questions: how the resident uses the device, and how active
  // the family is around them. Same load, switched client-side.
  const [view, setView] = useState("geraet");
  const [sessionChart, setSessionChart] = useState("verlauf");

  const load = useCallback(() => {
    let cancelled = false;
    fetchUsage(`/patient_analytics/${patientId}`, days)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [patientId, days]);

  useEffect(load, [load]);

  if (error) return <div className="empty-note">{error}</div>;
  if (!data) return <div className="empty-note">Lädt…</div>;

  const s = data.sessions;
  const lastSeen = formatLastInteraction(data.last_interaction);

  return (
    <div style={{ opacity: reloading ? 0.4 : 1, transition: "opacity 200ms ease" }}>
      <div className="topbar">
        <div className="topbar-left">
          <button className="back-link" onClick={onBack}>
            ← Alle Bewohner
          </button>
          <h1>
            {data.patient.forename} {data.patient.surname}
          </h1>
        </div>
        <div className="topbar-right">
          <button
            className="btn"
            onClick={() => {
              setReloading(true);
              load();
              setTimeout(() => setReloading(false), 260);
            }}>
            Neu laden
          </button>
          <RangeToggle days={days} onChange={onDaysChange} />
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 18 }}>
        <button className={view === "geraet" ? "active" : ""} onClick={() => setView("geraet")}>
          Gerätenutzung
        </button>
        <button className={view === "familie" ? "active" : ""} onClick={() => setView("familie")}>
          Familie
        </button>
      </div>

      {view === "familie" ? (
        <FamilyPanel data={data.family} />
      ) : (
      <div className="stack">
        <div className="stat-grid">
          <StatCard label="Sitzungen" value={formatCount(s.count)} />
          <StatCard label="Gesamtzeit" value={formatDuration(s.total_ms)} />
          <StatCard label="Median" value={formatDuration(s.median_ms)} />
          <StatCard label="Durchschnitt" value={formatDuration(s.mean_ms)} />
          <StatCard
            label="Wortwechsel"
            value={s.median_turns ?? "—"}
            extra={<span className="stat-context">Median</span>}
          />
          <StatCard label="Aktive Tage" value={formatCount(s.active_days)} />
          <StatCard
            label="Zuletzt aktiv"
            value={lastSeen.relative}
            extra={lastSeen.exact ? <span className="stat-context">{lastSeen.exact}</span> : null}
          />
        </div>

        <div className="card">
          <div className="card-title">
            <span>{sessionChart === "verlauf" ? "Sitzungszeit nach Tag" : "Sitzungen nach Tageszeit"}</span>
            <div className="segmented segmented-small">
              <button
                className={sessionChart === "verlauf" ? "active" : ""}
                onClick={() => setSessionChart("verlauf")}>
                Verlauf
              </button>
              <button
                className={sessionChart === "tageszeit" ? "active" : ""}
                onClick={() => setSessionChart("tageszeit")}>
                Tageszeit
              </button>
            </div>
          </div>
          {sessionChart === "verlauf" ? (
            <DailyDurationChart days={s.by_day} />
          ) : (
            <Histogram hours={s.by_hour} />
          )}
        </div>

        <div className="two-col">
          <div className="card">
            <div className="card-title">Gestartet durch</div>
            <ProgressList rows={s.by_initiator} labels={INITIATOR_LABELS} />
          </div>
          <div className="card">
            <div className="card-title">Beendet durch</div>
            <ProgressList rows={s.by_ended_by} labels={ENDED_BY_LABELS} />
          </div>
        </div>

        <div className="card">
          <div className="card-title">Vorgeschlagene Routinen</div>
          <RoutineOffers data={data.routine_offers} />
        </div>

        <div className="card">
          <div className="card-title">Nutzung im Detail</div>
          <BreakdownPanel
            dimensions={data.dimensions}
            config={DIMENSIONS}
            startSplitLabels={START_SPLIT_LABELS}
          />
        </div>
      </div>
      )}
    </div>
  );
}

export default function AdminUsageSection() {
  useManrope();
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    fetchUsage("/patient_analytics", days)
      .then((data) => {
        setPatients(data.patients || []);
        setError("");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(load, [load]);

  if (selected) {
    return (
      <div className="sc-usage">
        <ResidentDetail
          patientId={selected}
          days={days}
          onDaysChange={setDays}
          onBack={() => setSelected(null)}
        />
      </div>
    );
  }

  // Least recently active first — the reason to open this view.
  const sorted = [...patients].sort(
    (a, b) => (b.sessions.days_since_last ?? -1) - (a.sessions.days_since_last ?? -1),
  );

  return (
    <div className="sc-usage">
      <div className="topbar">
        <div className="topbar-left">
          <h1>Gerätenutzung</h1>
        </div>
        <div className="topbar-right">
          <RangeToggle days={days} onChange={setDays} />
        </div>
      </div>

      {loading ? (
        <div className="empty-note">Lädt…</div>
      ) : error ? (
        <div className="empty-note">{error}</div>
      ) : !sorted.length ? (
        <div className="empty-note">Keine Bewohner gefunden.</div>
      ) : (
        <div className="stack-8">
          {sorted.map((patient) => {
            const s = patient.sessions;
            const flag = attention(s);
            return (
              <button key={patient.id} className="res-row" onClick={() => setSelected(patient.id)}>
                <div>
                  <div className="res-name">
                    {patient.forename} {patient.surname}
                  </div>
                  <div className="res-sub">
                    #{patient.id}
                    {ageFromBirthdate(patient.birthdate) != null ? ` · ${ageFromBirthdate(patient.birthdate)} Jahre` : ""}
                  </div>
                </div>
                {[
                  ["Sitzungen", formatCount(s.count)],
                  ["Median", formatDuration(s.median_ms)],
                  ["Aktive Tage", formatCount(s.active_days)],
                  ["Zuletzt aktiv", formatLastInteraction(patient.last_interaction).relative],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="res-metric-label">{label}</div>
                    <div className="res-metric-value">{value}</div>
                  </div>
                ))}
                {flag ? <span className={`flag flag-${flag.tone}`}>{flag.label}</span> : <span />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
